import { Article } from '../models/article.model';
import { ArticleState } from '../states/article.state';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import {
    CreateArticle,
    CreateArticleFailure,
    CreateArticleSuccess,
    DeleteArticle,
    DeleteArticleFailure,
    DeleteArticleSuccess,
    FetchArticle,
    FetchArticleByUniqueId,
    FetchArticleByUniqueIdFailure,
    FetchArticleByUniqueIdSuccess,
    FetchArticleFailure,
    FetchArticles,
    FetchArticlesAuthors,
    FetchArticlesAuthorsFailure,
    FetchArticlesAuthorsSuccess,
    FetchArticlesFailure,
    FetchArticlesSuccess,
    FetchArticleSuccess,
    UpdateArticle,
    UpdateArticleFailure,
    UpdateArticleRelatedPostings,
    UpdateArticleRelatedPostingsFailure,
    UpdateArticleRelatedPostingsSuccess,
    UpdateArticleSuccess,
} from '../actions';
import { Injectable } from '@angular/core';
import { SortByAllArticles, SortLanguageAllArticles, SortOrderAllArticles } from '../actions/all-articles.action';
import { BehaviorSubject, Observable, race } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Posting } from 'app/library/posting/models/posting.model';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { GlobalParticipant } from 'app/projects/user/src/lib/models/participant.model';

@Injectable({
    providedIn: 'root',
})
export class ArticleService {
    @Select(ArticleState.getEntities)
    articles$: Observable<Article[]>;

    @Select(ArticleState.getEntity)
    currentArticle: Observable<Article>;

    tempSortBySelectedValue: any;
    tempSortOrderSelectedValue: any;

    private _saveArticleChanges = new BehaviorSubject<boolean>(null);
    public saveArticleChangesSubject$ = this._saveArticleChanges.asObservable();

    constructor(private _store: Store, private _action$: Actions, private _actionHelper: NgxsActionHelper) {}

    /**
     *
     */
    getCurrentArticle(): Article {
        return this._store.selectSnapshot(ArticleState.getEntity);
    }

    /**
     *
     */
    getArticles(): Article[] {
        return this._store.selectSnapshot(ArticleState.getEntities);
    }

    /**
     *
     * @param articleId
     */
    getArticleById(articleId: string): Article {
        return ArticleState.getEntity(this._store.selectSnapshot(ArticleState), articleId);
    }

    /**
     *
     */
    async fetchArticles(): Promise<boolean> {
        this._store.dispatch(new FetchArticles());

        return await this._actionHelper.race(FetchArticlesSuccess, FetchArticlesFailure).toPromise();
    }

    saveArticleChanges(value: boolean): void {
        this._saveArticleChanges.next(value);
    }

    /**
     *
     * @param articleId
     * @param force
     */
    async fetchArticleById(articleId: string, force: boolean = false): Promise<Article> {
        this._store.dispatch(new FetchArticle(articleId));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchArticleSuccess),
                map((action: FetchArticleSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchArticleFailure),
                map((action: FetchArticleFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchArticleByUniqueId(articleId: string, force: boolean = false): Promise<Article> {
        this._store.dispatch(new FetchArticleByUniqueId(articleId));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchArticleByUniqueIdSuccess),
                map((action: FetchArticleByUniqueIdSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchArticleByUniqueIdFailure),
                map((action: FetchArticleByUniqueIdFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchArticlesAuthors(): Promise<GlobalParticipant[]> {
        this._store.dispatch(new FetchArticlesAuthors());

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchArticlesAuthorsSuccess),
                map((action: FetchArticlesAuthorsSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchArticlesAuthorsFailure),
                map((action: FetchArticlesAuthorsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async updateArticle(article: Article): Promise<Article> {
        this._store.dispatch(new UpdateArticle(article));

        return race(
            this._action$.pipe(
                ofActionDispatched(UpdateArticleSuccess),
                map((action: UpdateArticleSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(UpdateArticleFailure),
                map((action: UpdateArticleFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async updateArticleRelatedPostings(id: string, relatedPostings: Posting[]): Promise<Article> {
        this._store.dispatch(new UpdateArticleRelatedPostings({ articleId: id, postings: relatedPostings }));

        return race(
            this._action$.pipe(
                ofActionDispatched(UpdateArticleRelatedPostingsSuccess),
                map((action: UpdateArticleRelatedPostingsSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(UpdateArticleRelatedPostingsFailure),
                map((action: UpdateArticleRelatedPostingsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    deleteArticle(articleId: string): Promise<boolean> {
        this._store.dispatch(new DeleteArticle(articleId));

        return race(
            this._action$.pipe(
                ofActionDispatched(DeleteArticleSuccess),
                map((action: DeleteArticleSuccess) => true)
            ),
            this._action$.pipe(
                ofActionDispatched(DeleteArticleFailure),
                map((action: DeleteArticleFailure) => false)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async createArticle(article: Article): Promise<Article> {
        this._store.dispatch(new CreateArticle(article));

        return race(
            this._action$.pipe(
                ofActionDispatched(CreateArticleSuccess),
                map((action: CreateArticleSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(CreateArticleFailure),
                map((action: CreateArticleFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async setSortBy(payload): Promise<any> {
        return this._store.dispatch(new SortByAllArticles(payload));
    }

    async setSortOder(payload): Promise<any> {
        return this._store.dispatch(new SortOrderAllArticles(payload));
    }

    async setSortLanguage(payload): Promise<any> {
        return this._store.dispatch(new SortLanguageAllArticles(payload));
    }
}
